import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { DateFnsModule } from 'ngx-date-fns';
import {
  SendoutComment,
  SendoutCommentsService,
  SendoutCommentThread,
} from '../../../services/sendout-comments/sendout-comments.service';
import { firstValueFrom, map, Observable } from 'rxjs';
import { UserType } from '../../../types';
import { FormFieldComponent } from '../../atoms/form-field/form-field.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { CommentInputComponent } from '../../atoms/comment-input/comment-input.component';

@Component({
  selector: 'app-sendout-comments',
  standalone: true,
  imports: [
    CommonModule,
    DateFnsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatExpansionModule,
    CommentInputComponent,
  ],
  templateUrl: './sendout-comments.component.html',
  styleUrl: './sendout-comments.component.css',
})
export class SendoutCommentsComponent
  implements OnInit, AfterViewInit, AfterViewChecked
{
  threads$: Observable<SendoutCommentThread[]> = null!;

  UserType = UserType;

  @Input() sendoutId: number = null!;

  @ViewChildren('commentThreads', { read: ElementRef })
  threadElements!: QueryList<ElementRef>;
  private observer?: IntersectionObserver;
  private timeoutMap = new Map<number, any>(); // threadId → timeout
  private readThreadIds = new Set<number>();
  private readonly delayMs = 3000; // 3 seconds

  constructor(
    private sendoutComments: SendoutCommentsService,
    private cd: ChangeDetectorRef
  ) {}
  ngOnInit(): void {
    if (this.sendoutId) {
      this.threads$ = this.sendoutComments
        .getCommentThreadsForSendout(this.sendoutId)
        .pipe(
          map((threads) => {
            return threads.map((t) => {
              return {
                ...t,
                comments: t.comments.map((c) => {
                  return {
                    ...c,
                  };
                }),
              };
            });
          })
        );
    }
  }
  ngAfterViewInit() {
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target as HTMLElement;
          const threadId = Number(el.dataset['threadId']);

          if (!threadId || this.readThreadIds.has(threadId)) continue;

          if (entry.isIntersecting) {
            // Start read timeout
            const timeout = setTimeout(async () => {
              await this.sendoutComments.markThreadRead(threadId);
              this.observer?.unobserve(el);
            }, this.delayMs);
            this.timeoutMap.set(threadId, timeout);
          } else {
            // User scrolled away – cancel pending read
            const timeout = this.timeoutMap.get(threadId);
            if (timeout) {
              clearTimeout(timeout);
              this.timeoutMap.delete(threadId);
            }
          }
        }
      },
      { threshold: 0.5 }
    ); // At least 50% visible

    // Observe all threads
    this.threadElements.forEach((el) => {
      this.observer?.observe(el.nativeElement);
    });
  }
  ngAfterViewChecked() {
    this.threadElements.forEach((el) => {
      this.observer?.observe(el.nativeElement);
    });
  }
  ngOnDestroy() {
    this.observer?.disconnect();
    this.timeoutMap.forEach((timeout) => clearTimeout(timeout));
  }
  async onReply(comment: string, threadId: number, parentId?: number) {
    await firstValueFrom(
      this.sendoutComments.addCommentForSendout(
        this.sendoutId,
        comment,
        threadId,
        parentId
      )
    );
  }

  async onThread(comment: string) {
    await firstValueFrom(
      this.sendoutComments.addCommentForSendout(this.sendoutId, comment)
    );
  }
}
